/*
 * SyncHandshakeCard — shown inline on each connected supplier card on
 * the buyer's Store Setup tab.
 *
 * 3 chips:
 *   ✓ Buyer Shopify    — buyer has at least one connected dropship_store
 *   ✓ Seller Shopify   — seller has at least one connected dropship_store
 *   ✓ Catalog syncing  — buyer has enabled ≥ 1 product from this seller
 *
 * When all 3 are green: 🟢 "Ready to trade" badge.
 * When any are red: 🟡 "Sync incomplete" badge with a list of fixes.
 *
 * Polls every 30s while mounted so the buyer sees state updates without
 * having to refresh.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Check, AlertCircle, RefreshCw, Circle } from "lucide-react";

export default function SyncHandshakeCard({ counterpartId, role = "buyer" }) {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get(`/dropship/connection-health/${counterpartId}`)
       .then(r => setHealth(r.data))
       .catch(() => {})
       .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [counterpartId]);

  if (!health && loading) {
    return (
      <div className="rounded-xl border border-border bg-secondary/40 p-3 text-[12px] text-foreground/55">
        Checking sync health…
      </div>
    );
  }
  if (!health) return null;

  const ready = health.ready_to_trade;
  const lastSync = health.last_sync_at ? new Date(health.last_sync_at) : null;
  const lastSyncDisplay = lastSync ? _relativeTime(lastSync) : "never";

  // For the buyer-facing card, "yourself" chip is the buyer side. For the
  // (rare) seller-facing rendering, flip the labels.
  const meChip = role === "buyer" ? "buyer" : "seller";
  const themChip = role === "buyer" ? "seller" : "buyer";

  return (
    <div className={`rounded-xl border p-3 ${ready ? "border-emerald-200 bg-emerald-50/50" : "border-amber-200 bg-amber-50/40"}`}
         data-testid={`sync-handshake-${counterpartId}`}>
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`text-[10.5px] mono uppercase tracking-[0.16em] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${
          ready ? "bg-emerald-600 text-white" : "bg-amber-500 text-white"
        }`}>
          {ready
            ? <><Check className="w-2.5 h-2.5 stroke-[3]"/>Ready to trade</>
            : <><AlertCircle className="w-2.5 h-2.5"/>Sync incomplete</>}
        </div>
        <div className="text-[11px] text-foreground/55 mono">
          last sync · {lastSyncDisplay}
        </div>
        <button onClick={load} title="Refresh sync status"
                data-testid={`refresh-sync-${counterpartId}`}
                className="ml-auto text-foreground/40 hover:text-foreground p-1">
          <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/>
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Chip
          label="Your channel"
          ok={role === "buyer" ? (health.buyer_channel_connected ?? health.buyer_shopify_connected) : (health.seller_channel_connected ?? health.seller_shopify_connected)}
          subtitle={role === "buyer" ? (health.buyer_channel_kind || null) : (health.seller_channel_kind || null)}
          dataTestid={`chip-${meChip}-channel-${counterpartId}`}
        />
        <Chip
          label={`${role === "buyer" ? "Seller" : "Buyer"} channel`}
          ok={role === "buyer" ? (health.seller_channel_connected ?? health.seller_shopify_connected) : (health.buyer_channel_connected ?? health.buyer_shopify_connected)}
          subtitle={role === "buyer" ? (health.seller_channel_kind || null) : (health.buyer_channel_kind || null)}
          dataTestid={`chip-${themChip}-channel-${counterpartId}`}
        />
        <Chip
          label="Catalog syncing"
          ok={health.catalog_syncing}
          subtitle={health.products_enabled_count > 0
            ? `${health.products_enabled_count} ${health.products_enabled_count === 1 ? "product" : "products"}`
            : null}
          dataTestid={`chip-catalog-${counterpartId}`}
        />
      </div>

      {!ready && health.blocking_issues?.length > 0 && (
        <div className="mt-2.5 pt-2.5 border-t border-amber-200/70 text-[11.5px] text-amber-900">
          {(health.blocking_issues.includes("buyer_channel_not_connected") || health.blocking_issues.includes("buyer_shopify_not_connected")) && role === "buyer" && (
            <div>· Connect your sales channel first.</div>
          )}
          {(health.blocking_issues.includes("seller_channel_not_connected") || health.blocking_issues.includes("seller_shopify_not_connected")) && role === "buyer" && (
            <div>· Waiting on the seller to connect their sales channel.</div>
          )}
          {health.blocking_issues.includes("no_products_enabled") && role === "buyer" && (
            <div>· Enable at least one product from this seller&apos;s catalog.</div>
          )}
        </div>
      )}
    </div>
  );
}

function Chip({ label, ok, subtitle, dataTestid }) {
  return (
    <div data-testid={dataTestid}
         className={`rounded-lg border px-2.5 py-2 ${
           ok ? "bg-white border-emerald-200" : "bg-white border-foreground/10 opacity-70"
         }`}>
      <div className="flex items-center gap-1.5">
        {ok
          ? <Check className="w-3.5 h-3.5 text-emerald-600 stroke-[3]"/>
          : <Circle className="w-3.5 h-3.5 text-foreground/30"/>}
        <span className={`text-[11.5px] ${ok ? "text-foreground/85" : "text-foreground/45"}`}>{label}</span>
      </div>
      {subtitle && (
        <div className="text-[10px] mono text-foreground/45 mt-0.5 ml-5">{subtitle}</div>
      )}
    </div>
  );
}

function _relativeTime(d) {
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return "just now";
  const mins = Math.round(diff / 60_000);
  if (mins < 60)   return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24)  return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7)    return `${days}d ago`;
  return d.toLocaleDateString();
}
